import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useGuildRights } from '@/lib/access';
import {
  POSTED_ENTRY,
  POSTED_LABEL,
  editPath,
  postedKey,
  postedSummary,
  usePosted,
  type PostedMessage,
  type PostedType,
} from '@/lib/posted';
import { Button } from '@/components/ui/button';
import { UserMention } from './UserMention';

/**
 * "Bereits gepostet" — the posted copies of a panel / message type with
 * Aktualisieren, Löschen and "In Discord öffnen". Used on every panel page and
 * (all types) on the Gepostete-Nachrichten page.
 *
 *   getUpdateData   editor content to update with (tickets, self-roles,
 *                   embeds). Without it the bot renders from the saved panel.
 *   updateBlocked   reason the update button is disabled (e.g. unsaved edits)
 *   onLoad          "Laden": put this post's content into the editor
 *   showEdit        "Bearbeiten" link to the matching editor
 */
export function PostedList({
  guildId,
  type,
  getUpdateData,
  updateBlocked,
  onLoad,
  onChanged,
  showEdit = false,
  showType = false,
  emptyText = 'Noch nichts gepostet.',
}: {
  guildId: string;
  type?: PostedType;
  getUpdateData?: () => Record<string, unknown>;
  updateBlocked?: string | null;
  onLoad?: (p: PostedMessage) => void;
  /** After a successful update/delete (e.g. self-roles bindings changed too). */
  onChanged?: () => void;
  showEdit?: boolean;
  showType?: boolean;
  emptyText?: string;
}) {
  const posted = usePosted(guildId, type);
  const channels = useQuery({
    queryKey: ['guild-channels', guildId],
    queryFn: () => api<{ channels: Array<{ id: string; name: string }> }>(`/api/guilds/${guildId}/channels`),
    staleTime: 30_000,
  });
  const channelName = (id: string) => channels.data?.channels.find((c) => c.id === id)?.name ?? id;

  if (posted.isLoading) return <p className="text-sm text-muted-foreground">Lade gepostete Nachrichten…</p>;
  if (posted.error) return <p className="text-sm text-destructive">{(posted.error as Error).message}</p>;
  const rows = posted.data?.posted ?? [];
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{emptyText}</p>;

  return (
    <div>
      {updateBlocked && <p className="mb-1 text-xs text-muted-foreground">{updateBlocked}</p>}
      <ul className="divide-y">
        {rows.map((p) => (
          <PostedRow
            key={p.id}
            guildId={guildId}
            post={p}
            channel={channelName(p.channelId)}
            getUpdateData={getUpdateData}
            updateBlocked={updateBlocked}
            onLoad={onLoad}
            onChanged={onChanged}
            showEdit={showEdit}
            showType={showType}
          />
        ))}
      </ul>
    </div>
  );
}

function PostedRow({
  guildId,
  post,
  channel,
  getUpdateData,
  updateBlocked,
  onLoad,
  onChanged,
  showEdit,
  showType,
}: {
  guildId: string;
  post: PostedMessage;
  channel: string;
  getUpdateData?: () => Record<string, unknown>;
  updateBlocked?: string | null;
  onLoad?: (p: PostedMessage) => void;
  onChanged?: () => void;
  showEdit: boolean;
  showType: boolean;
}) {
  const qc = useQueryClient();
  const { can } = useGuildRights(guildId);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const canEdit = can(POSTED_ENTRY[post.type], 'edit');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: postedKey(guildId) });
    onChanged?.();
  };

  const update = useMutation({
    mutationFn: () =>
      api<{ updated?: boolean; queued?: boolean; message?: string; reactionWarnings?: string[] }>(
        `/api/guilds/${guildId}/posted/${post.id}/update`,
        { method: 'POST', body: JSON.stringify(getUpdateData ? { data: getUpdateData() } : {}) }
      ),
    onSuccess: (r) => {
      setNote(
        r.queued
          ? { ok: true, text: 'An den Bot geschickt — er hat noch nicht geantwortet.' }
          : r.reactionWarnings?.length
            ? { ok: false, text: `Aktualisiert, aber Reaktionen fehlen: ${r.reactionWarnings.join(', ')}` }
            : { ok: true, text: 'Aktualisiert ✓' }
      );
      refresh();
    },
    onError: (err) => setNote({ ok: false, text: (err as Error).message }),
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/guilds/${guildId}/posted/${post.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (err) => setNote({ ok: false, text: (err as Error).message }),
  });

  const url = `https://discord.com/channels/${post.guildId}/${post.channelId}/${post.messageId}`;
  const edit = showEdit ? editPath(post) : null;
  const updatable = post.type !== 'nsfw';
  const summary = postedSummary(post);

  return (
    <li className="flex flex-wrap items-center gap-2 py-2 text-sm">
      <div className="min-w-0 flex-1 basis-64">
        <div className="font-medium">
          {showType && <span className="mr-1.5">{POSTED_LABEL[post.type]} ·</span>}#{channel}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {new Date(post.createdAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
          {post.postedBy ? (
            <>
              {' · von '}
              <UserMention id={post.postedBy} />
            </>
          ) : post.adopted ? (
            ' · per Link übernommen'
          ) : null}
          {summary && ` · ${summary}`}
        </div>
        {note && <div className={note.ok ? 'text-xs text-success' : 'text-xs text-destructive'}>{note.text}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {onLoad && canEdit && (
          <Button variant="ghost" size="sm" onClick={() => onLoad(post)} title="Inhalt in den Editor laden">
            <Download className="h-3.5 w-3.5" /> Laden
          </Button>
        )}
        {edit && canEdit && (
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/server/${guildId}${edit}`}>
              <Pencil className="h-3.5 w-3.5" /> Bearbeiten
            </Link>
          </Button>
        )}
        {updatable && canEdit && (
          <Button
            variant="outline"
            size="sm"
            disabled={update.isPending || !!updateBlocked}
            title={updateBlocked ?? 'Nachricht in Discord mit dem aktuellen Stand überschreiben'}
            onClick={() => {
              setNote(null);
              update.mutate();
            }}
          >
            <RefreshCw className={update.isPending ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            Aktualisieren
          </Button>
        )}
        <Button variant="ghost" size="sm" asChild>
          <a href={url} target="_blank" rel="noreferrer" title="In Discord öffnen">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            disabled={remove.isPending}
            title="In Discord löschen"
            onClick={() => {
              if (confirm(`${POSTED_LABEL[post.type]} in #${channel} wirklich in Discord löschen?`)) remove.mutate();
            }}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        )}
      </div>
    </li>
  );
}
